import { Stack, Text, Group, Box, Button, SimpleGrid, UnstyledButton } from '@mantine/core';
import classes from './PromptLibraryModal.module.css';
import { useUserSettingsStore } from '../../../../stores/userSettingsStore';
import { useState, useMemo } from 'react';
import { Icon } from '../Icon/Icon';
import { FEATURES } from '../../../../config/featureFlags';
import { IconPencil } from '@tabler/icons-react';

interface PromptTemplate {
  id: string;
  text: string;
  label?: string;
}

interface PromptLibraryModalProps {
  onPromptSelect?: (prompt: PromptTemplate) => void;
  onClose?: () => void;
}

const PROMPT_TEMPLATES: PromptTemplate[] = [
  {
    id: 'proofread',
    text: 'Proofread the following text and outline any issues with spelling, grammar, or clarity: [text]'
  },
  {
    id: 'email',
    text: 'Write me a professional email based on the following bullet points: [text]'
  },
  {
    id: 'summarise-research',
    text: 'Summarise the key points and arguments of the following research paper: [text]'
  },
  {
    id: 'summarise-content',
    text: 'Summarise the following content, prioritising brevity and clarity: [text]'
  },
  {
    id: 'research-questions',
    text: "I'm researching [text]. Suggest potential research questions or hypotheses."
  },
  {
    id: 'explain-concept',
    text: 'Explain the following concept in simple terms: [text]'
  },
  {
    id: 'translate',
    text: 'Translate the following content from Mandarin to British English: [text]'
  },
  {
    id: 'code-bugs',
    text: 'Analyse this code and suggest fixes for any located bugs: [text]'
  },
  {
    id: 'explain-code',
    text: 'Explain this code step by step: [text]'
  }
];

export const PromptLibraryModal = ({ onPromptSelect, onClose }: PromptLibraryModalProps) => {
  const [activeTab, setActiveTab] = useState('templates')
  const [editingPromptText, setEditingPromptText] = useState<string | null>(null);
  const [labelDraft, setLabelDraft] = useState('');
  const savedMessages = useUserSettingsStore((state) => state.userSettings?.savedMessages || []);
  const { removeMessage, updateMessageLabel } = useUserSettingsStore();
  const handlePromptClick = (prompt: PromptTemplate) => {
    onPromptSelect?.(prompt);
  };

  const savedPrompts = useMemo(() => {
    return savedMessages.map((message) => {
      if (typeof message === 'string') {
        return {
          id: message,
          text: message,
        };
      }

      return {
        id: message.id || message.text,
        text: message.text,
        label: message.label,
      };
    });
  }, [savedMessages]);

  const openLabelEditor = (prompt: PromptTemplate) => {
    setEditingPromptText(prompt.text);
    setLabelDraft(prompt.label || '');
  };

  const closeLabelEditor = () => {
    setEditingPromptText(null);
    setLabelDraft('');
  };

  const saveLabel = () => {
    if (!editingPromptText) {
      return;
    }

    void updateMessageLabel(editingPromptText, labelDraft);
    closeLabelEditor();
  };

  if (!FEATURES.showSavePrompts) {
    return (
      <Box className={classes.modal}>
        <Stack gap={36}>
          {/* Header */}
          <Text className={classes.title}>Prompt Library</Text>

          {/* Tab */}
          <Group>
            <Button
              className={`${classes.tab} ${activeTab === 'templates' ? classes.activeTab : ''}`}
              onClick={() => setActiveTab('templates')}
            >
              Templates & Examples
            </Button>
          </Group>

          {/* Prompt Grid */}
          <Box className={classes.gridContainer}>
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing={18} verticalSpacing={18}>
              {PROMPT_TEMPLATES.map((prompt) => (
                <UnstyledButton
                  key={prompt.id}
                  className={classes.promptCard}
                  onClick={() => handlePromptClick(prompt)}
                >
                  <Box className={classes.promptContent}>
                    <Text className={classes.promptText}>{prompt.text}</Text>
                    <Text className={classes.promptAuthorText}>Suggested by Co-op GPT</Text>
                  </Box>
                </UnstyledButton>
              ))}
            </SimpleGrid>
          </Box>
        </Stack>
      </Box>
    )
  }

  return (
    <Box className={classes.modal}>
      <Stack gap={36}>
        {/* Header */}
        <Text className={classes.title}>Prompt Library</Text>

        {/* Tab */}
        <Group>
          <Button
            className={`${classes.tab} ${activeTab === 'templates' ? classes.activeTab : ''}`}
            onClick={() => setActiveTab('templates')}
          >
            Templates & Examples
          </Button>
          <Button
            className={`${classes.tab} ${activeTab === 'saved' ? classes.activeTab : ''}`}
            onClick={() => setActiveTab('saved')}
          >
            Your Saved Prompts
          </Button>
        </Group>

        {/* Prompt Grid */}
        <Box className={classes.gridContainer}>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing={18} verticalSpacing={18}>
            {activeTab === 'templates'
              ? PROMPT_TEMPLATES.map((prompt) => (
                <UnstyledButton
                  key={prompt.id}
                  className={classes.promptCard}
                  onClick={() => handlePromptClick(prompt)}
                >
                  <Box className={classes.promptContent}>
                    <Text className={classes.promptText}>{prompt.text}</Text>
                    <Text className={classes.promptAuthorText}>Suggested by Co-op GPT</Text>
                  </Box>
                </UnstyledButton>
              ))
              : savedPrompts.length === 0 ? (
                  <Text key="no-saved-prompts" className={classes.noSavedPromptsText}>
                    You haven’t saved any prompts yet.
                  </Text>
                ) : savedPrompts.map((prompt) => (
                  (
                <UnstyledButton
                  key={prompt.id}
                  className={classes.promptCard}
                  onClick={() => handlePromptClick(prompt)}
                >
                  <Box className={classes.promptContent}>
                    <Text className={classes.promptText}>{prompt.label || prompt.text}</Text>
                    <Group className={classes.cardFooter}>
                      <Text className={classes.promptAuthorText}>Saved by You</Text>
                      <Group className={classes.actionButtons} gap={4}>
                        <Button
                          className={classes.iconButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            openLabelEditor(prompt);
                          }}
                          aria-label={`Edit label for saved prompt`}
                        >
                          <IconPencil size={16} />
                        </Button>
                        <Button
                          className={classes.iconButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            removeMessage(prompt.text);
                          }}
                          aria-label={`Delete saved prompt`}
                        >
                          <Icon
                            name="trash"
                            className={classes.deleteIcon}
                          />
                        </Button>
                      </Group>
                    </Group>
                  </Box>
                  {editingPromptText === prompt.text && (
                    <Box
                      className={classes.editOverlay}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Text className={classes.editOverlayTitle}>Add Label</Text>
                      <input
                        className={classes.editOverlayInput}
                        value={labelDraft}
                        onChange={(e) => setLabelDraft(e.target.value)}
                        placeholder="Enter prompt label"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void saveLabel();
                          }

                          if (e.key === 'Escape') {
                            e.preventDefault();
                            closeLabelEditor();
                          }
                        }}
                        autoFocus
                      />
                      <Group className={classes.editOverlayActions}>
                        <Button
                          className={classes.editOverlayActionButton}
                          variant="default"
                          onClick={closeLabelEditor}
                        >
                          Cancel
                        </Button>
                        <Button
                          className={classes.editOverlayActionButton}
                          onClick={() => void saveLabel()}
                        >
                          Save
                        </Button>
                      </Group>
                    </Box>
                  )}
                </UnstyledButton>
                )
              ))
            }
          </SimpleGrid>
        </Box>
      </Stack>
    </Box>
  );
};

export type { PromptTemplate, PromptLibraryModalProps };
